// Formato de metraje en OCTAVOS DE PÁGINA, como en Movie Magic.
//
// El sector no escribe "12/8", escribe "1 4/8": una página y cuatro octavos. Un director de
// producción lee la segunda forma de un vistazo y la primera le hace calcular. Lo pidió un
// cliente (7-ago-2026) y tiene razón: es el estándar con el que trabajan.
//
//   4  → "4/8"        (menos de una página)
//   8  → "1"          (página justa)
//   12 → "1 4/8"
//   80 → "10"
//
// `conUnidad` añade "pág" para los totales, donde el número suelto se puede confundir con
// otra cosa ("10 pág" en vez de "10").
export function formatEighths(eighths: number | null | undefined, conUnidad = false): string {
  const n = Math.max(0, Math.round(Number(eighths) || 0));
  if (n === 0) return conUnidad ? '0 pág' : '0';
  if (n < 8) return `${n}/8`;
  const paginas = Math.floor(n / 8);
  const resto = n % 8;
  const base = resto === 0 ? `${paginas}` : `${paginas} ${resto}/8`;
  return conUnidad ? `${base} pág` : base;
}

// Total de una lista de escenas, ya formateado.
export function formatEighthsTotal(escenas: Array<{ page_eighths?: number | null }>): string {
  return formatEighths(escenas.reduce((s, e) => s + (e.page_eighths ?? 0), 0), true);
}

// ── Cruce de dos personajes ─────────────────────────────────────────
// Cuántas escenas comparten dos personajes y, sobre todo, CUÁNTA PELÍCULA es eso.
// La tarjeta de Casting pintaba "40 escenas (63%)" con el porcentaje calculado sobre
// el número de escenas, y se leía como porcentaje de película. No lo es: en un caso
// real dos protagonistas compartían 40 de 63 escenas (63%), pero esas 40 escenas eran
// 530 de los 758 octavos del guion, o sea el 70% de las páginas, que es la cifra que
// el productor tiene en la cabeza cuando dice "casi toda la peli son ellos dos".

export interface EscenaConOctavos {
  id: string;
  page_eighths?: number | null;
}

export interface CrucePersonajes {
  escenasCompartidas: number;
  totalEscenas: number;
  octavosCompartidos: number;
  totalOctavos: number;
  pctEscenas: number;
  pctPaginas: number;
}

export function cruceDePersonajes(
  escenas: EscenaConOctavos[],
  idsA: Iterable<string>,
  idsB: Iterable<string>,
): CrucePersonajes {
  const setA = new Set(idsA);
  const setB = new Set(idsB);
  let escenasCompartidas = 0;
  let octavosCompartidos = 0;
  let totalOctavos = 0;
  const vistas = new Set<string>();
  for (const e of escenas ?? []) {
    if (!e || vistas.has(e.id)) continue;   // una escena repetida no cuenta dos veces
    vistas.add(e.id);
    const octavos = Math.max(0, Number(e.page_eighths) || 0);
    totalOctavos += octavos;
    if (setA.has(e.id) && setB.has(e.id)) {
      escenasCompartidas++;
      octavosCompartidos += octavos;
    }
  }
  const totalEscenas = vistas.size;
  const pctEscenas = totalEscenas > 0 ? Math.round((escenasCompartidas / totalEscenas) * 100) : 0;
  // Sin octavos en el desglose (guion sin metraje medido) el porcentaje de páginas no
  // se puede calcular: se cae al de escenas, que es la única señal disponible.
  const pctPaginas = totalOctavos > 0
    ? Math.round((octavosCompartidos / totalOctavos) * 100)
    : pctEscenas;
  return { escenasCompartidas, totalEscenas, octavosCompartidos, totalOctavos, pctEscenas, pctPaginas };
}
